import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  Link as MuiLink,
  Tooltip,
  LinearProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  InputAdornment
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  School as SchoolIcon,
  AttachMoney as AttachMoneyIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  WhatsApp as WhatsAppIcon,
  Payment as PaymentIcon,
  BarChart as BarChartIcon,
  CalendarToday as CalendarIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { teacherService} from '../../services/api';
import { teacherPaymentService } from '../../services/teacherPaymentService';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const months = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const TeacherDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [teacher, setTeacher] = useState<any>(null);
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [salaryCalculation, setSalaryCalculation] = useState<any>(null);
  
  // Datos para nuevo pago
  const [newPayment, setNewPayment] = useState({
    month: dayjs().format('MMMM').toLowerCase(),
    year: dayjs().year(),
    hours_worked: 0,
    extra_bonus: 0,
    deductions: 0,
    description: ''
  });

  // Estadísticas
  const [stats, setStats] = useState({
    totalPaid: 0,
    totalHours: 0,
    averageMonthly: 0,
    paymentsCount: 0,
    monthlyData: [] as any[],
    yearlyData: [] as any[]
  });

  useEffect(() => {
    loadTeacherData();
  }, [id]);

  const loadTeacherData = async () => {
    try {
      setLoading(true);
      const [teacherRes, paymentsRes, summaryRes] = await Promise.all([
        teacherService.getById(parseInt(id!)),
        teacherService.getPayments(parseInt(id!)),
        teacherService.getPaymentSummary(parseInt(id!))
      ]);
      
      setTeacher(teacherRes.data);
      setPayments(paymentsRes.data.payments?.data || []);
      
      // Calcular estadísticas
      const paidPayments = paymentsRes.data.payments?.data?.filter((p: any) => p.status === 'paid') || [];
      const totalPaid = paidPayments.reduce((sum: number, p: any) => sum + parseFloat(p.total_amount), 0);
      const totalHours = paidPayments.reduce((sum: number, p: any) => sum + parseFloat(p.hours_worked), 0);
      
      // Preparar datos mensuales para gráficos
      const monthlyData = months.map((month, index) => {
        const monthPayments = paidPayments.filter((p: any) => 
          p.month.toLowerCase() === month.toLowerCase() && p.year === dayjs().year()
        );
        const total = monthPayments.reduce((sum: any, p: { total_amount: number; }) => sum + p.total_amount, 0);
        const hours = monthPayments.reduce((sum: any, p: { hours_worked: number; }) => sum + p.hours_worked, 0);
        
        return {
          name: month.substring(0, 3),
          month: index + 1,
          total,
          hours,
          count: monthPayments.length
        };
      });
      
      setStats({
        totalPaid,
        totalHours,
        averageMonthly: paidPayments.length > 0 ? parseFloat(totalPaid) / paidPayments.length : 0,
        paymentsCount: paidPayments.length,
        monthlyData,
        yearlyData: summaryRes.data?.monthly_summary || []
      });
      
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateSalary = async () => {
    try {
      const response = await teacherService.calculateSalary(parseInt(id!), newPayment);
      setSalaryCalculation(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al calcular salario');
    }
  };

  const handleCreatePayment = async () => {
    try {
      await teacherPaymentService.create({
        teacher_id: parseInt(id!),
        ...newPayment,
        hourly_rate: teacher.hourly_rate,
        total_amount: salaryCalculation?.calculation?.total_amount || 0,
        payment_date: dayjs().format('YYYY-MM-DD'),
        status: 'pending'
      });
      
      setPaymentDialogOpen(false);
      loadTeacherData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al crear pago');
    }
  };

  const handleSendWhatsApp = () => {
    if (!teacher?.phone) return;
    
    const message = `Hola ${teacher.first_name}, te escribo desde el Sistema Financiero Escuela Origen.`;
    window.open(`https://wa.me/${teacher.phone.replace(/\+/g, '')}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleSendEmail = () => {
    if (!teacher?.email) return;
    
    const subject = 'Información sobre pagos';
    const body = `Estimado/a ${teacher.first_name} ${teacher.last_name},\n\n`;
    window.location.href = `mailto:${teacher.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const paymentColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'month_year',
      headerName: 'Período',
      width: 120,
      //valueGetter: (params) => `${params.row.month} ${params.row.year}`
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.month}/{params.row.year}
        </Typography>
      )
    },
    {
      field: 'hours_worked',
      headerName: 'Horas',
      width: 80,
      align: 'center',
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'hourly_rate',
      headerName: 'Tarifa',
      width: 100,
      align: 'right',
      renderCell: (params) => (
        <Typography variant="body2">
          ₽{params.value}
        </Typography>
      )
    },
    {
      field: 'total_amount',
      headerName: 'Total',
      width: 120,
      align: 'right',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium" color="primary">
          ₽{params.value.toLocaleString()}
        </Typography>
      )
    },
    {
      field: 'payment_date',
      headerName: 'Fecha Pago',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.payment_date 
            ? dayjs(params.row.payment_date).format('DD/MM/YYYY')
            : '-'
          }
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Estado',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.row.status === 'paid' ? 'Pagado' : 
                 params.row.status === 'pending' ? 'Pendiente' : params.row.status}
          color={params.row.status === 'paid' ? 'success' : 
                 params.row.status === 'pending' ? 'warning' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 100,
      renderCell: (params) => (
        <Button
          size="small"
          onClick={() => navigate(`/teacher-payments/${params.id}`)}
        >
          Ver
        </Button>
      )
    }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !teacher) {
    return (
      <Alert severity="error">
        {error || 'Profesor no encontrado'}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <IconButton component={Link} to="/teachers" sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Avatar sx={{ width: 56, height: 56, mr: 2, bgcolor: 'secondary.main' }}>
            <PersonIcon fontSize="large" />
          </Avatar>
          <Box>
            <Typography variant="h4" component="h1">
              {teacher.first_name} {teacher.last_name}
            </Typography>
            {teacher.specialization && (
              <Typography variant="subtitle1" color="text.secondary">
                {teacher.specialization}
              </Typography>
            )}
          </Box>
          <Chip
            icon={teacher.status === 'active' ? <CheckCircleIcon /> : <WarningIcon />}
            label={teacher.status === 'active' ? 'Activo' : 'Inactivo'}
            color={teacher.status === 'active' ? 'success' : 'default'}
            sx={{ ml: 2 }}
          />
        </Box>
        
        <Box>
          <Tooltip title="Enviar WhatsApp">
            <IconButton 
              onClick={handleSendWhatsApp}
              disabled={!teacher.phone}
              sx={{ mr: 1 }}
              color="success"
            >
              <WhatsAppIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Enviar Email">
            <IconButton 
              onClick={handleSendEmail}
              disabled={!teacher.email}
              sx={{ mr: 1 }}
              color="primary"
            >
              <EmailIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<PaymentIcon />}
            onClick={() => setPaymentDialogOpen(true)}
            sx={{ mr: 1 }}
            disabled={!['admin', 'manager'].includes(user?.role || '')}
          >
            Registrar Pago
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/teachers/${id}/edit`)}
            disabled={!['admin', 'manager'].includes(user?.role || '')}
          >
            Editar
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 3, sm: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Total Pagado
              </Typography>
              <Typography variant="h4" color="primary">
                ₽{stats.totalPaid.toLocaleString()}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={75} 
                sx={{ mt: 2, height: 4, borderRadius: 2 }}
              />
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Total Horas
              </Typography>
              <Typography variant="h4">
                {stats.totalHours}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Promedio: {(stats.totalHours / (stats.paymentsCount || 1)).toFixed(1)} hrs/pago
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Tarifa por Hora
              </Typography>
              <Typography variant="h4" color="success.main">
                ₽{teacher.hourly_rate}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Monto base por hora
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Pagos Realizados
              </Typography>
              <Typography variant="h4">
                {stats.paymentsCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Promedio: ₽{stats.averageMonthly.toFixed(0)} por pago
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Información General" />
          <Tab label="Historial de Pagos" />
          <Tab label="Estadísticas" />
          <Tab label="Análisis" />
        </Tabs>
      </Paper>

      {/* Tab: Información General */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Información de Contacto
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ width: '30%', bgcolor: 'action.hover' }}>
                          <Typography variant="subtitle2">Email</Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            <MuiLink href={`mailto:${teacher.email}`}>
                              {teacher.email}
                            </MuiLink>
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ bgcolor: 'action.hover' }}>
                          <Typography variant="subtitle2">Teléfono</Typography>
                        </TableCell>
                        <TableCell>
                          {teacher.phone ? (
                            <Box display="flex" alignItems="center">
                              <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                              <MuiLink href={`tel:${teacher.phone}`}>
                                {teacher.phone}
                              </MuiLink>
                            </Box>
                          ) : (
                            'No especificado'
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Información Profesional
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ width: '30%', bgcolor: 'action.hover' }}>
                          <Typography variant="subtitle2">Especialización</Typography>
                        </TableCell>
                        <TableCell>
                          {teacher.specialization || 'No especificada'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ bgcolor: 'action.hover' }}>
                          <Typography variant="subtitle2">Tarifa por Hora</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={<AttachMoneyIcon />}
                            label={`₽${teacher.hourly_rate}`}
                            color="primary"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ bgcolor: 'action.hover' }}>
                          <Typography variant="subtitle2">Fecha de Registro</Typography>
                        </TableCell>
                        <TableCell>
                          {dayjs(teacher.created_at).format('DD/MM/YYYY')}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Tab: Historial de Pagos */}
      <TabPanel value={tabValue} index={1}>
        <Paper sx={{ height: 500 }}>
          <DataGrid
            rows={payments}
            columns={paymentColumns}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 }
              }
            }}
            onRowClick={(params) => navigate(`/teacher-payments/${params.id}`)}
            localeText={{
              noRowsLabel: 'No hay pagos registrados'
            }}
          />
        </Paper>
      </TabPanel>

      {/* Tab: Estadísticas */}
      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Pagos Mensuales - {dayjs().year()}
                </Typography>
                <Box height={300}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
                      <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
                      <RechartsTooltip />
                      <Legend />
                      <Bar yAxisId="left" dataKey="total" fill="#8884d8" name="Monto (RUB)" />
                      <Bar yAxisId="right" dataKey="hours" fill="#82ca9d" name="Horas" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Distribución de Pagos
                </Typography>
                <Box height={300}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={[
                            { name: 'Pagados', value: payments.filter(p => p.status === 'paid').length },
                            { name: 'Pendientes', value: payments.filter(p => p.status === 'pending').length }
                            ]}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => {
                            // CORRECCIÓN: Verificamos que percent exista y sea un número
                            const percentage = percent ? (percent * 100).toFixed(0) : '0';
                            return `${name}: ${percentage}%`;
                            }}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                        >
                            {[0, 1].map((index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <RechartsTooltip 
                            formatter={(value: number | undefined, name: string | undefined) => [
                                `${value ?? 0} pago${(value ?? 0) !== 1 ? 's' : ''}`,
                                name ?? '',
                            ]}
                        />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Resumen por Año
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Año</TableCell>
                        <TableCell align="right">Total Horas</TableCell>
                        <TableCell align="right">Total Pagado</TableCell>
                        <TableCell align="right">Cantidad Pagos</TableCell>
                        <TableCell align="right">Promedio por Pago</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stats.yearlyData.map((yearData: any) => (
                        <TableRow key={yearData.year}>
                          <TableCell>{yearData.year}</TableCell>
                          <TableCell align="right">{yearData.total_hours || 0}</TableCell>
                          <TableCell align="right">₽{(yearData.total_paid || 0).toLocaleString()}</TableCell>
                          <TableCell align="right">{yearData.payment_count || 0}</TableCell>
                          <TableCell align="right">
                            ₽{((yearData.total_paid || 0) / (yearData.payment_count || 1)).toFixed(0)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Tab: Análisis */}
      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Proyección Anual
                </Typography>
                <Box height={300}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={stats.monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Line type="monotone" dataKey="total" stroke="#8884d8" name="Monto Real" />
                      <Line type="monotone" dataKey="hours" stroke="#82ca9d" name="Horas" />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Análisis de Eficiencia
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell>Promedio Mensual</TableCell>
                        <TableCell align="right">₽{stats.averageMonthly.toFixed(0)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Ratio Hora/Pago</TableCell>
                        <TableCell align="right">
                          {(stats.totalHours / (stats.paymentsCount || 1)).toFixed(1)} hrs
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Mejor Mes</TableCell>
                        <TableCell align="right">
                          {stats.monthlyData.reduce((max, data) => 
                            data.total > max.total ? data : max
                          , { total: 0, name: '' }).name || 'N/A'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Proyección Anual</TableCell>
                        <TableCell align="right" color="primary">
                          ₽{(stats.averageMonthly * 12).toFixed(0)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Dialog para nuevo pago */}
      <Dialog 
        open={paymentDialogOpen} 
        onClose={() => setPaymentDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <PaymentIcon sx={{ mr: 1 }} />
            Registrar Pago a Profesor
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                fullWidth
                label="Mes"
                value={newPayment.month}
                onChange={(e) => setNewPayment({...newPayment, month: e.target.value})}
              >
                {months.map((month) => (
                  <MenuItem key={month} value={month.toLowerCase()}>
                    {month}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Año"
                value={newPayment.year}
                onChange={(e) => setNewPayment({...newPayment, year: parseInt(e.target.value)})}
              />
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Horas Trabajadas"
                value={newPayment.hours_worked}
                onChange={(e) => {
                  setNewPayment({...newPayment, hours_worked: parseFloat(e.target.value)});
                  setTimeout(handleCalculateSalary, 100);
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">horas</InputAdornment>
                }}
              />
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Bonificación Extra"
                value={newPayment.extra_bonus}
                onChange={(e) => {
                  setNewPayment({...newPayment, extra_bonus: parseFloat(e.target.value)});
                  setTimeout(handleCalculateSalary, 100);
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₽</InputAdornment>
                }}
              />
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Deducciones"
                value={newPayment.deductions}
                onChange={(e) => {
                  setNewPayment({...newPayment, deductions: parseFloat(e.target.value)});
                  setTimeout(handleCalculateSalary, 100);
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₽</InputAdornment>
                }}
              />
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Descripción"
                value={newPayment.description}
                onChange={(e) => setNewPayment({...newPayment, description: e.target.value})}
              />
            </Grid>

            {salaryCalculation && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="info">
                  <Typography variant="subtitle2">Cálculo de Salario:</Typography>
                  <Typography variant="body2">
                    {salaryCalculation.calculation.hours_worked} horas × ₽{salaryCalculation.calculation.hourly_rate} = ₽{salaryCalculation.calculation.base_salary}
                  </Typography>
                  {salaryCalculation.calculation.extra_bonus > 0 && (
                    <Typography variant="body2">
                      + Bonificación: ₽{salaryCalculation.calculation.extra_bonus}
                    </Typography>
                  )}
                  {salaryCalculation.calculation.deductions > 0 && (
                    <Typography variant="body2">
                      - Deducciones: ₽{salaryCalculation.calculation.deductions}
                    </Typography>
                  )}
                  <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                    Total: ₽{salaryCalculation.calculation.total_amount}
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>Cancelar</Button>
          <Button 
            onClick={handleCreatePayment} 
            variant="contained"
            disabled={!salaryCalculation || newPayment.hours_worked <= 0}
          >
            Registrar Pago
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeacherDetail;